import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppsT } from '@/lib/appsI18n';
import AppShell from '@/pages/client/apps/components/AppShell';
import { localGet, localId, useAppData, formatDateTime } from '@/lib/appStorage';
import { fetchDocuments } from '@/lib/documents';
import { docHasText, extractDocText } from '@/lib/docText';
import { supabase } from '@/lib/supabase';
import { splitSentences, type NotesState } from '@/pages/client/apps/notes/notesUtils';

interface Source {
  id: string;
  kind: 'note' | 'document';
  title: string;
  text: string;
}

interface Reference {
  title: string;
  excerpt: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  references?: Reference[];
  at: string;
}

interface Conversation {
  id: string;
  title: string;
  sourceIds: string[];
  messages: Message[];
  createdAt: string;
}

interface StudyState {
  conversations: Conversation[];
}

const EMPTY: StudyState = { conversations: [] };

const STOP_WORDS = new Set([
  'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'en', 'à', 'au', 'aux', 'pour', 'par',
  'est', 'sont', 'que', 'qui', 'dans', 'sur', 'avec', 'plus', 'ce', 'cette', 'il', 'elle', 'on',
  'je', 'tu', 'nous', 'vous', 'mon', 'ma', 'mes', 'quel', 'quelle', 'quoi', 'comment', 'pourquoi',
]);

function keywords(text: string): string[] {
  return (text.toLowerCase().match(/[a-zà-ÿ]{4,}/g) || []).filter((word) => !STOP_WORDS.has(word));
}

function rankSentences(sources: Source[], question: string) {
  const words = keywords(question);
  const scored: { source: Source; sentence: string; score: number }[] = [];
  sources.forEach((source) => {
    splitSentences(source.text).forEach((sentence) => {
      const lower = sentence.toLowerCase();
      let score = 0;
      words.forEach((word) => {
        if (lower.includes(word)) score += 2;
      });
      if (score > 0) scored.push({ source, sentence, score });
    });
  });
  return scored.sort((a, b) => b.score - a.score);
}

/** Réponse locale extractive : uniquement quand des sources sont cochées. */
function buildLocalAnswer(sources: Source[], question: string): { content: string; references: Reference[] } {
  const ranked = rankSentences(sources, question);
  if (ranked.length === 0) {
    return {
      content:
        "Je n'ai trouvé aucun passage de vos sources qui réponde à cette question. Reformulez, ou ajoutez une source.",
      references: [],
    };
  }
  const top = ranked.slice(0, 5);
  return {
    content: [
      `D'après ${Array.from(new Set(top.map((item) => item.source.title))).join(', ')} :`,
      ...top.map((item) => `• ${item.sentence}`),
      '',
      'Reformulez ces points à voix haute, puis vérifiez avec la source citée.',
    ].join('\n'),
    references: top.slice(0, 3).map((item) => ({ title: item.source.title, excerpt: item.sentence })),
  };
}

const IMAGE_LINE = /(\/\/[^\s)]+\.(?:png|jpe?g|webp|gif|svg)(?:\?[^\s)]*)?)|(https?:\/\/[^\s)]+\.(?:png|jpe?g|webp|gif|svg)(?:\?[^\s)]*)?)/i;

/** Rendu d'un message : paragraphes, puces, images markdown ou URL d'image. */
function MessageBody({ content }: { content: string }) {
  const lines = content.split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;
        const mdImage = trimmed.match(/^!\[(.*?)\]\((https?:\/\/[^\s)]+)\)$/);
        if (mdImage) {
          return (
            <img
              key={index}
              src={mdImage[2]}
              alt={mdImage[1] || 'Illustration'}
              className="max-h-64 w-auto rounded-lg border border-background-200/60"
            />
          );
        }
        if (IMAGE_LINE.test(trimmed) && trimmed.split(/\s+/).length <= 2) {
          return (
            <img
              key={index}
              src={trimmed}
              alt="Illustration"
              className="max-h-64 w-auto rounded-lg border border-background-200/60"
            />
          );
        }
        if (/^[•\-*]\s+/.test(trimmed)) {
          return (
            <p key={index} className="flex gap-2 text-sm leading-relaxed">
              <span className="text-accent-600">•</span>
              <span>{trimmed.replace(/^[•\-*]\s+/, '')}</span>
            </p>
          );
        }
        return (
          <p key={index} className="text-sm leading-relaxed whitespace-pre-wrap break-words">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}

export default function ZifekStudyAiPage() {
  const { user } = useAuth();
  const { t, isRtl } = useAppsT();
  const storage = useAppData<StudyState>('client_study_ai_v3', EMPTY, { scope: 'user', userId: user?.id });
  const { data, setData, ready } = storage;

  const [sources, setSources] = useState<Source[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingSources, setPendingSources] = useState<string[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);

  const loadSources = useCallback(async () => {
    if (!user) {
      setSourcesLoading(false);
      return;
    }
    setSourcesLoading(true);
    const collected: Source[] = [];
    try {
      const notes =
        (await localGet<NotesState>(`user::${user.id}:client_notes_v2`))
        || (await localGet<NotesState>(`user::${user.id}:client_notes`));
      (notes?.notes || []).forEach((note) => {
        if ((note.content || '').trim().length < 40) return;
        collected.push({ id: `note:${note.id}`, kind: 'note', title: note.title || t('notes.untitled'), text: note.content });
      });
    } catch {
      /* les notes sont optionnelles */
    }
    try {
      const docs = await fetchDocuments(user.id);
      docs.filter((doc) => docHasText(doc)).slice(0, 40).forEach((doc) => {
        collected.push({ id: `doc:${doc.id}`, kind: 'document', title: doc.name, text: extractDocText(doc).slice(0, 8000) });
      });
    } catch {
      /* les documents sont optionnels */
    }
    setSources(collected);
    setSourcesLoading(false);
  }, [user, t]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  // Sécurité : ne jamais rester bloqué sur un écran de chargement.
  useEffect(() => {
    const timer = window.setTimeout(() => setSourcesLoading(false), 6000);
    return () => window.clearTimeout(timer);
  }, []);

  const conversations = data.conversations || [];
  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeId) || null,
    [conversations, activeId],
  );

  const selectedSources = useMemo(
    () => sources.filter((source) => selectedSourceIds.includes(source.id)),
    [sources, selectedSourceIds],
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [activeConversation?.messages.length, thinking]);

  const openConversation = (conversation: Conversation) => {
    setActiveId(conversation.id);
    setSelectedSourceIds(conversation.sourceIds || []);
    setSidebarOpen(false);
    setError(null);
  };

  const openNewChat = () => {
    setPendingSources([]);
    setNewChatOpen(true);
  };

  const createConversation = () => {
    const conversation: Conversation = {
      id: localId('conv'),
      title: pendingSources.length > 0 ? t('ai.newChat') : t('ai.newChat'),
      sourceIds: pendingSources,
      messages: [],
      createdAt: new Date().toISOString(),
    };
    setData((prev) => ({ conversations: [conversation, ...(prev.conversations || [])] }));
    setActiveId(conversation.id);
    setSelectedSourceIds(pendingSources);
    setNewChatOpen(false);
    setSidebarOpen(false);
    setError(null);
  };

  const deleteConversation = (id: string) => {
    if (!window.confirm(`${t('ai.delete')} ?`)) return;
    setData((prev) => ({ conversations: prev.conversations.filter((conversation) => conversation.id !== id) }));
    if (activeId === id) setActiveId(null);
  };

  const toggleSource = (id: string) => {
    setSelectedSourceIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const askRemote = async (asked: string, context: Source[]): Promise<string | null> => {
    try {
      const call = supabase.functions
        .invoke('zifek-client-chat', {
          body: {
            message: asked,
            context: context.map((source) => ({ title: source.title, text: source.text.slice(0, 3000) })),
            mode: context.length > 0 ? 'study' : 'general',
            assistant: 'study-ai',
          },
        })
        .then(({ data: response, error: fnError }) => {
          if (fnError) return null;
          const payload = response as { reply?: string; answer?: string; message?: string } | null;
          const text = payload?.reply || payload?.answer || payload?.message;
          return typeof text === 'string' && text.trim().length > 0 ? text.trim() : null;
        })
        .catch(() => null);
      const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 25000));
      return await Promise.race([call, timeout]);
    } catch {
      return null;
    }
  };

  const send = async () => {
    const asked = question.trim();
    if (!asked || thinking) return;

    let conversationId = activeConversation?.id ?? null;
    if (!conversationId) {
      const conversation: Conversation = {
        id: localId('conv'),
        title: asked.slice(0, 48),
        sourceIds: selectedSourceIds,
        messages: [],
        createdAt: new Date().toISOString(),
      };
      conversationId = conversation.id;
      setData((prev) => ({ conversations: [conversation, ...(prev.conversations || [])] }));
      setActiveId(conversation.id);
    }

    const userMessage: Message = { id: localId('msg'), role: 'user', content: asked, at: new Date().toISOString() };
    const context = selectedSources;
    const targetId = conversationId;

    setQuestion('');
    setError(null);
    setThinking(true);

    setData((prev) => ({
      conversations: prev.conversations.map((conversation) =>
        conversation.id === targetId
          ? {
              ...conversation,
              title: conversation.messages.length === 0 ? asked.slice(0, 48) : conversation.title,
              sourceIds: selectedSourceIds,
              messages: [...conversation.messages, userMessage],
            }
          : conversation,
      ),
    }));

    const remote = await askRemote(asked, context);
    let answer: { content: string; references: Reference[] };

    if (remote) {
      answer = { content: remote, references: context.length > 0 ? buildLocalAnswer(context, asked).references : [] };
    } else if (context.length > 0) {
      answer = buildLocalAnswer(context, asked);
    } else {
      answer = {
        content:
          "Le service IA est momentanément injoignable. Vérifiez votre connexion et réessayez — ou sélectionnez une source pour obtenir une réponse locale immédiate.",
        references: [],
      };
      setError('Le service IA est injoignable pour le moment.');
    }

    const assistantMessage: Message = {
      id: localId('msg'),
      role: 'assistant',
      content: answer.content,
      references: answer.references,
      at: new Date().toISOString(),
    };

    setData((prev) => ({
      conversations: prev.conversations.map((conversation) =>
        conversation.id === targetId
          ? { ...conversation, messages: [...conversation.messages, assistantMessage] }
          : conversation,
      ),
    }));
    setThinking(false);
  };

  const suggestions = selectedSourceIds.length > 0
    ? ['Résume cette leçon', 'Explique les notions importantes', 'Crée 5 questions de révision', 'Propose un plan de révision']
    : ['Explique-moi les dérivées simplement', 'Donne-moi une méthode de révision efficace', 'Aide-moi à organiser ma semaine'];

  const showLoader = !ready && sourcesLoading;

  return (
    <AppShell slug="zifek-study-ai" sync={{ offline: storage.offline, syncing: storage.syncing, pending: storage.pending, syncError: storage.syncError, onSync: storage.forceSync }}>
      {showLoader ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className={`flex flex-col lg:flex-row gap-3 lg:gap-4 min-w-0 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
          {/* Colonne gauche : conversations */}
          <aside className={`${sidebarOpen ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-[260px] xl:w-[290px] flex-shrink-0 bg-background-50 border border-background-200/50 rounded-2xl overflow-hidden max-h-[420px] lg:max-h-[calc(100dvh-9.5rem)]`}>
            <div className="p-2.5 border-b border-background-200/50">
              <button
                onClick={openNewChat}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
              >
                <i className="ri-add-line"></i>
                {t('ai.newChat')}
              </button>
            </div>

            <p className="px-3 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
              {t('ai.history')} · {conversations.length}
            </p>
            <div className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-2 space-y-0.5">
              {conversations.length === 0 && (
                <p className="text-[11px] text-foreground-400 px-2 py-3">{t('ai.noConversation')}</p>
              )}
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => openConversation(conversation)}
                  className={`group flex items-start gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                    activeId === conversation.id ? 'bg-background-100' : 'hover:bg-background-100/60'
                  }`}
                >
                  <i className="ri-chat-3-line text-foreground-400 text-xs mt-0.5"></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-foreground-800 truncate">{conversation.title}</p>
                    <p className="text-[10px] text-foreground-400 truncate">
                      {conversation.sourceIds.length > 0
                        ? `${conversation.sourceIds.length} ${t('ai.sources').toLowerCase()}`
                        : t('ai.noSources')}
                      {' · '}
                      {formatDateTime(conversation.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteConversation(conversation.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-red-600 cursor-pointer"
                    title={t('ai.delete')}
                  >
                    <i className="ri-delete-bin-line text-[11px]"></i>
                  </button>
                </div>
              ))}
            </div>

            <div className="p-2.5 border-t border-background-200/50">
              <button
                onClick={() => setLibraryOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-[11px] font-medium cursor-pointer hover:bg-background-200/70"
              >
                <i className="ri-folder-open-line"></i>
                <span className="flex-1 text-left truncate">{t('ai.sources')}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-background-50 text-[10px] font-bold">
                  {selectedSourceIds.length}/{sources.length}
                </span>
              </button>
            </div>
          </aside>

          {/* Colonne droite : chat */}
          <section className="flex-1 min-w-0 bg-background-50 border border-background-200/50 rounded-2xl flex flex-col overflow-hidden h-[calc(100dvh-12rem)] min-h-[420px] lg:h-[calc(100dvh-9.5rem)]">
            <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-background-200/50 flex-shrink-0">
              <button
                onClick={() => setSidebarOpen((value) => !value)}
                className="lg:hidden w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center cursor-pointer flex-shrink-0"
                title={t('ai.history')}
              >
                <i className="ri-history-line"></i>
              </button>
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
                <i className="ri-sparkling-line"></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold font-heading text-foreground-950 truncate">
                  {activeConversation?.title || t('ai.title')}
                </p>
                <p className="text-[10px] text-foreground-500 truncate">
                  {selectedSourceIds.length > 0 ? t('ai.studyMode') : t('ai.generalMode')}
                </p>
              </div>
              <button
                onClick={() => setLibraryOpen(true)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer ${
                  selectedSourceIds.length > 0
                    ? 'bg-accent-100 text-accent-900 hover:bg-accent-200/70'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className="ri-attachment-2"></i>
                <span className="hidden sm:inline">
                  {selectedSourceIds.length > 0 ? `${selectedSourceIds.length} ${t('ai.sources').toLowerCase()}` : t('ai.noSources')}
                </span>
                <span className="sm:hidden">{selectedSourceIds.length}</span>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 py-4 space-y-4">
              {!activeConversation || activeConversation.messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-accent-100 text-accent-800 flex items-center justify-center mb-3">
                    <i className="ri-sparkling-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-950">{t('ai.title')}</h3>
                  <p className="text-xs text-foreground-500 mt-1.5 max-w-md">{t('ai.empty')}</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setQuestion(suggestion)}
                        className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium cursor-pointer hover:bg-background-200/70"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                  {sources.length === 0 && (
                    <p className="text-[11px] text-foreground-400 mt-4 max-w-sm">{t('ai.noSourcesFound')}</p>
                  )}
                </div>
              ) : (
                activeConversation.messages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[92%] sm:max-w-[85%] min-w-0">
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 ${
                          message.role === 'user'
                            ? 'bg-foreground-950 text-background-50 rounded-br-md'
                            : 'bg-background-100/70 text-foreground-800 rounded-bl-md'
                        }`}
                      >
                        {message.role === 'user' ? (
                          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
                        ) : (
                          <MessageBody content={message.content} />
                        )}
                      </div>
                      {message.references && message.references.length > 0 && (
                        <div className="mt-1.5 ps-1 space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
                            {t('ai.references')}
                          </p>
                          {message.references.map((reference, index) => (
                            <div key={index} className="text-[10px] text-foreground-500 bg-background-50 border border-background-200/60 rounded-md px-2 py-1">
                              <span className="font-semibold text-foreground-700">{reference.title}</span> — {reference.excerpt.slice(0, 120)}…
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {thinking && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-md px-4 py-2.5 bg-background-100/70 text-foreground-500 text-sm flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('ai.analyse')}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {error && (
              <div className="mx-3 mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[11px] flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                <span className="flex-1">{error}</span>
                <button onClick={() => setError(null)} className="cursor-pointer"><i className="ri-close-line"></i></button>
              </div>
            )}

            <div className="border-t border-background-200/50 p-2.5 sm:p-3 flex-shrink-0">
              <div className="flex items-end gap-2">
                <button
                  onClick={() => setLibraryOpen(true)}
                  className="w-10 h-10 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-200/70 flex-shrink-0"
                  title={t('ai.sources')}
                >
                  <i className="ri-attachment-2"></i>
                </button>
                <textarea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                  rows={1}
                  placeholder={selectedSourceIds.length > 0 ? t('ai.placeholderSources') : t('ai.placeholder')}
                  className="flex-1 min-w-0 max-h-28 px-3.5 py-2.5 rounded-2xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 resize-none"
                />
                <button
                  onClick={() => void send()}
                  disabled={!question.trim() || thinking}
                  className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-40 flex-shrink-0"
                  title={t('ai.send')}
                >
                  <i className={thinking ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
                </button>
              </div>
              <p className="text-[10px] text-foreground-400 mt-1.5 truncate">
                {selectedSourceIds.length > 0 ? t('ai.studyMode') : t('ai.generalMode')}
              </p>
            </div>
          </section>
        </div>
      )}

      {/* Modal : nouvelle conversation avec choix des sources */}
      {newChatOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setNewChatOpen(false)} aria-hidden="true" />
          <div className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-t-2xl sm:rounded-2xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-bold font-heading text-foreground-950">{t('ai.newChat')}</h3>
                <p className="text-[11px] text-foreground-500 mt-0.5">
                  Choisissez sur quoi l’IA doit s’appuyer pour cette conversation.
                </p>
              </div>
              <button
                onClick={() => setNewChatOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
                title={t('app.close')}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <button
              onClick={() => setPendingSources([])}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left cursor-pointer transition-colors mb-2 ${
                pendingSources.length === 0 ? 'border-accent-300 bg-accent-50' : 'border-background-200/70 hover:bg-background-100/60'
              }`}
            >
              <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-foreground-900">IA générale (par défaut)</span>
                <span className="block text-[11px] text-foreground-500">
                  L’IA répond à tous les sujets, sans limite de sources.
                </span>
              </span>
              {pendingSources.length === 0 && <i className="ri-check-line text-accent-600"></i>}
            </button>

            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400 mt-3 mb-1.5">
              Ou s’appuyer sur mes fichiers
            </p>
            {sourcesLoading ? (
              <div className="flex items-center gap-2 text-[11px] text-foreground-400 py-3">
                <i className="ri-loader-4-line animate-spin"></i>Chargement de vos documents…
              </div>
            ) : sources.length === 0 ? (
              <p className="text-[11px] text-foreground-400 px-1 py-3">{t('ai.noSourcesFound')}</p>
            ) : (
              <div className="rounded-xl border border-background-200/60 divide-y divide-background-200/40 max-h-60 overflow-y-auto">
                {sources.map((source) => (
                  <label
                    key={source.id}
                    className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer ${
                      pendingSources.includes(source.id) ? 'bg-accent-50/70' : 'hover:bg-background-100/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={pendingSources.includes(source.id)}
                      onChange={() =>
                        setPendingSources((prev) =>
                          prev.includes(source.id) ? prev.filter((value) => value !== source.id) : [...prev, source.id],
                        )
                      }
                      className="mt-0.5 w-3.5 h-3.5 accent-accent-600 cursor-pointer"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-medium text-foreground-800 truncate">{source.title}</span>
                      <span className="block text-[10px] text-foreground-400">
                        {source.kind === 'note' ? 'Note' : 'Document'} · {source.text.length} car.
                      </span>
                    </span>
                    <i className={`${source.kind === 'note' ? 'ri-sticky-note-line' : 'ri-file-text-line'} text-foreground-400 text-sm`}></i>
                  </label>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 mt-4">
              <span className="text-[11px] text-foreground-500">
                {pendingSources.length === 0 ? 'Mode IA générale' : `${pendingSources.length} source(s) sélectionnée(s)`}
              </span>
              <button
                onClick={createConversation}
                className="px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
              >
                Créer la conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bibliothèque de sources de la conversation en cours */}
      {libraryOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setLibraryOpen(false)} aria-hidden="true" />
          <div className="relative w-full sm:max-w-lg max-h-[88vh] overflow-y-auto bg-background-50 rounded-t-2xl sm:rounded-2xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-bold font-heading text-foreground-950">{t('ai.sources')}</h3>
                <p className="text-[11px] text-foreground-500 mt-0.5">
                  Aucune sélection = IA générale. Chaque conversation garde ses propres sources.
                </p>
              </div>
              <button
                onClick={() => setLibraryOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
                title={t('app.close')}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <button
              onClick={() => {
                setSelectedSourceIds([]);
                setLibraryOpen(false);
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left cursor-pointer transition-colors mb-2 ${
                selectedSourceIds.length === 0 ? 'border-accent-300 bg-accent-50' : 'border-background-200/70 hover:bg-background-100/60'
              }`}
            >
              <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-foreground-900">Aucune source — IA générale</span>
                <span className="block text-[11px] text-foreground-500">L’IA répond à tous les sujets.</span>
              </span>
              {selectedSourceIds.length === 0 && <i className="ri-check-line text-accent-600"></i>}
            </button>

            {sources.length === 0 ? (
              <p className="text-[11px] text-foreground-400 px-1 py-4">{t('ai.noSourcesFound')}</p>
            ) : (
              <div className="rounded-xl border border-background-200/60 divide-y divide-background-200/40 max-h-72 overflow-y-auto">
                {sources.map((source) => (
                  <label
                    key={source.id}
                    className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer ${
                      selectedSourceIds.includes(source.id) ? 'bg-accent-50/70' : 'hover:bg-background-100/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSourceIds.includes(source.id)}
                      onChange={() => toggleSource(source.id)}
                      className="mt-0.5 w-3.5 h-3.5 accent-accent-600 cursor-pointer"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-medium text-foreground-800 truncate">{source.title}</span>
                      <span className="block text-[10px] text-foreground-400">
                        {source.kind === 'note' ? 'Note' : 'Document'} · {source.text.length} car.
                      </span>
                    </span>
                    <i className={`${source.kind === 'note' ? 'ri-sticky-note-line' : 'ri-file-text-line'} text-foreground-400 text-sm`}></i>
                  </label>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 mt-4">
              {selectedSourceIds.length > 0 && (
                <button
                  onClick={() => setSelectedSourceIds([])}
                  className="text-[11px] font-medium text-foreground-400 hover:text-red-600 cursor-pointer"
                >
                  {t('ai.revoke')}
                </button>
              )}
              <div className="flex-1"></div>
              <button
                onClick={() => setLibraryOpen(false)}
                className="px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
              >
                {t('app.save')} ({selectedSourceIds.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}